import React, { useState } from 'react'
import { ZoomIn, ZoomOut, RotateCw, Download } from 'lucide-react'

interface Highlight {
  id: string
  text: string
  startIndex: number
  length: number
  page: number
}

interface ImageViewerProps {
  imageUrl: string
  fileName?: string
  highlights?: Highlight[]
  onHighlightClick?: (highlight: Highlight) => void
  className?: string
}

export const ImageViewer: React.FC<ImageViewerProps> = ({
  imageUrl,
  fileName = 'image',
  highlights = [],
  onHighlightClick,
  className = ''
}) => {
  const [zoom, setZoom] = useState(75)  // Start at 75% for better fit
  const [rotation, setRotation] = useState(0)
  const [isOverlayVisible, setIsOverlayVisible] = useState(true)

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50))
  }

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360)
  }

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = imageUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (!imageUrl) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <p className="text-gray-500">No image available</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative h-full flex flex-col bg-gray-100 ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Zoom Out"
            disabled={zoom <= 50}
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium min-w-[60px] text-center">
            {zoom}%
          </span>
          <button
            onClick={handleZoomIn}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Zoom In"
            disabled={zoom >= 200}
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <div className="h-6 w-px bg-gray-300 mx-2" />
          <button
            onClick={handleRotate}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
            title="Rotate"
          >
            <RotateCw className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={handleDownload}
          className="p-2 hover:bg-gray-100 rounded transition-colors flex items-center gap-2"
          title="Download"
        >
          <Download className="w-4 h-4" />
          <span className="text-sm">Download</span>
        </button>
      </div>

      {/* Image Container */}
      <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50">
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            src={imageUrl}
            alt={fileName}
            style={{
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              transition: 'transform 0.2s ease-in-out',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain'
            }}
            className="shadow-lg"
          />
        </div>
      </div>

      {/* Reasoning overlay — open by default, collapses into a floating bubble */}
      {highlights.length > 0 && (
        isOverlayVisible ? (
          <div className="absolute top-20 right-4 bg-purple-50 border border-purple-200 rounded-lg p-3 max-w-xs shadow-lg z-10">
            {/* Collapse-to-bubble button */}
            <button
              onClick={() => setIsOverlayVisible(false)}
              className="absolute top-2 right-2 text-purple-300 hover:text-purple-600 transition-colors"
              aria-label="Collapse reasoning"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
              </svg>
            </button>

            <div className="flex items-center gap-2 mb-2 pr-6">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              <div className="text-sm font-semibold text-purple-800">
                Reasoning
              </div>
            </div>
            <div className="text-xs text-purple-600 mb-1">
              Found in this image
            </div>
            <div className="mt-2 text-xs text-purple-900 bg-white p-2 rounded border border-purple-100">
              <div className="font-medium mb-1">Evidence:</div>
              <div className="italic">
                "{highlights[0].text.substring(0, 200)}{highlights[0].text.length > 200 ? '...' : ''}"
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsOverlayVisible(true)}
            className="absolute top-20 right-4 w-11 h-11 rounded-full bg-purple-500 hover:bg-purple-600 text-white shadow-lg z-10 flex items-center justify-center transition-colors"
            aria-label="Show reasoning"
            title="Reasoning"
          >
            <span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-50 animate-ping"></span>
            <svg className="w-5 h-5 relative" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </button>
        )
      )}
    </div>
  )
}

export default ImageViewer
